import React, { createContext, useContext, useState, useEffect } from 'react';
import LicenseManager from '../utils/LicenseManager';

const LicenseContext = createContext();

// Create single instance of license manager
const licenseManager = new LicenseManager();

// Only the workspace owner is billed. Every other role (manager, cleaner,
// employee) inherits the owner's license — they should never see the
// trial / activation flow. Reading straight from localStorage keeps this
// context free of a circular dependency on AuthContext.
const isWorkspaceOwner = () => {
  try {
    const raw = localStorage.getItem('maidly_current_user');
    if (!raw) return true; // Unauthenticated boot — let the normal gate run
    const role = JSON.parse(raw)?.role;
    return role === 'owner' || role === 'admin';
  } catch {
    return true;
  }
};

// State returned for team members — always "valid" so no gate ever fires.
const INHERITED_STATUS = {
  hasLicense: true,
  isValid: true,
  isTrialMode: false,
  trialDaysRemaining: 0,
  licenseDaysRemaining: 0,
  expiryDate: null,
  licenseKey: null,
  isExpired: false,
  needsActivation: false,
  loading: false,
  inherited: true,
};

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within LicenseProvider');
  }
  return context;
};

export const LicenseProvider = ({ children }) => {
  const [licenseStatus, setLicenseStatus] = useState({
    hasLicense: false,
    isValid: false,
    isTrialMode: false,
    trialDaysRemaining: 0,
    licenseDaysRemaining: 0,
    expiryDate: null,
    licenseKey: null,
    isExpired: false,
    needsActivation: false,
    loading: true
  });

  // Activate license using LicenseManager
  const activateLicense = async (licenseKey) => {
    const result = await licenseManager.activate(licenseKey);
    
    if (result.success) {
      // Update state from manager
      const info = licenseManager.getLicenseInfo();
      setLicenseStatus({
        hasLicense: true,
        isValid: true,
        isTrialMode: false,
        trialDaysRemaining: 0,
        licenseDaysRemaining: info.daysRemaining,
        expiryDate: info.expiryDate,
        licenseKey: info.licenseKey,
        isExpired: false,
        needsActivation: false,
        loading: false
      });
    }
    
    return result;
  };

  // Remove license using LicenseManager
  const removeLicense = () => {
    licenseManager.deactivate().catch(() => {});
    checkLicenseStatus();
  };

  // Check license status using LicenseManager
  const checkLicenseStatus = async () => {
    // Non-owner: skip everything, return an "inherited" valid status.
    if (!isWorkspaceOwner()) {
      setLicenseStatus(INHERITED_STATUS);
      return;
    }

    setLicenseStatus(prev => ({ ...prev, loading: true }));

    await licenseManager.initialize();
    const info = licenseManager.getLicenseInfo();
    
    setLicenseStatus({
      hasLicense: info.isValid,
      isValid: info.isValid,
      isTrialMode: info.isTrialMode,
      trialDaysRemaining: info.trialDaysRemaining,
      licenseDaysRemaining: info.daysRemaining,
      expiryDate: info.expiryDate,
      licenseKey: info.licenseKey,
      isExpired: info.isExpired,
      needsActivation: info.needsActivation,
      loading: false
    });
  };

  useEffect(() => {
    checkLicenseStatus();

    // Re-check whenever the auth state changes (login as owner vs employee).
    const onAuth = () => checkLicenseStatus();
    window.addEventListener('maidly:auth-changed', onAuth);
    window.addEventListener('storage', onAuth);

    // Check every 24 hours
    const interval = setInterval(checkLicenseStatus, 24 * 60 * 60 * 1000);
    return () => {
      clearInterval(interval);
      window.removeEventListener('maidly:auth-changed', onAuth);
      window.removeEventListener('storage', onAuth);
    };
  }, []);

  const value = {
    ...licenseStatus,
    activateLicense,
    removeLicense,
    refreshLicenseStatus: checkLicenseStatus
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};
